import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Beforeunload } from 'react-beforeunload';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LabeledStepper from 'studio/components/LabeledStepper/LabeledStepper';
import Content from 'studio/components/Content/Content';
import ChooseDataSource from './ChooseDataSource/ChooseDataSource';
import CreateKnowledgeGraph from './CreateKnowledgeGraph';
import ImportTables from './ImportTables';
import { trackEvent } from 'utils/eventTracking';
import styles from './index.module.scss';
import services from 'services';
import Alert from 'studio/components/Alert';
import { formatDate, formatQuantity, pluralize as plrl } from 'studio/helpers';
import Link from 'studio/components/Link';
import { paths as adminPaths } from 'studio/App/Admin/Admin';

const excelConnector = {
  type: 'EXCEL_CONNECTOR',
  id: 'excel',
  name: 'Excel'
};

const NewKnowledgeGraph = ({ languages, orgSettings, fetchKnowledgeGraphs, editorRef }) => {
  useEffect(() => {
    trackEvent('Studio New Knowledge Graph Opened');
  }, []);

  const [step, setStep] = useState(1);
  const [connectors, setConnectors] = useState(null);
  const [knowledgeGraph, setKnowledgeGraph] = useState(null);
  const [connector, setConnector] = useState(null);
  const [modal, setModal] = useState({ open: false, message: 'modal message' });
  const [fetchingKnowledgeGraphs, setFetchingKnowledgeGraphs] = useState(false);
  // the subscription
  const [subscription, setSubscription] = useState(null);

  const history = useHistory();
  const location = useLocation();

  // the subscription's quantities
  const quantities = useMemo(() => subscription?.data.quantities, [subscription]);
  // the subscription's upcoming configuration (if any)
  const upcomingConfiguration = useMemo(() => subscription?.data.upcomingConfiguration, [subscription]);
  // specifies whether the remaining knowledge graph quantity is sufficient to cover the new knowledge graph
  const remainingQuantityIsSufficient = useMemo(() => quantities?.remaining.knowledgeGraph > 0, [quantities]);
  // tooltip to show on the subcomponents' "Next" button if disabled
  const nextButtonTooltip = useMemo(
    () => (remainingQuantityIsSufficient ? '' : 'You have no more remaining knowledge graphs in your subscription'),
    [remainingQuantityIsSufficient]
  );
  // info/warning alert displaying information about the remaining knowledge graph quantity
  const alert = useMemo(
    () =>
      !!quantities && (
        <Alert severity={remainingQuantityIsSufficient ? 'info' : 'warning'} key="alert">
          <p>
            Your subscription includes
            {isFinite(quantities.knowledgeGraph) ? (
              <>
                {' '}
                <b>{formatQuantity(quantities.knowledgeGraph, 'Knowledge Graph')}</b>,{' '}
              </>
            ) : (
              <>
                {' '}
                an <b>unlimited</b> number of Knowledge Graphs.
              </>
            )}
            {isFinite(quantities.remaining.knowledgeGraph) && (
              <>
                of which there {plrl(quantities.remaining.knowledgeGraph, 'is', 'are')}{' '}
                <b>{formatQuantity(quantities.remaining.knowledgeGraph)}</b> remaining.
              </>
            )}
          </p>
          {!subscription.data.isEnterprise && (
            <p>
              Go to{' '}
              <Link href={adminPaths.billing} newWindow>
                Billing
              </Link>{' '}
              and
              {remainingQuantityIsSufficient ? <> update </> : <> upgrade </>}
              your subscription&apos;s plan to
              {remainingQuantityIsSufficient ? <> change </> : <> increase </>}
              the number of included Knowledge Graphs.
            </p>
          )}
        </Alert>
      ),
    [quantities, remainingQuantityIsSufficient]
  );
  // warning alert informing the user if creating the knowledge graph would remove the subscription's scheduled update
  const scheduledUpdateAlert = useMemo(() => {
    // only show the alert if we are able to create a knowledge graph...
    const showAlert =
      remainingQuantityIsSufficient &&
      // ...and there is a scheduled update with an insufficient remaining knowledge graph quantity
      !!upcomingConfiguration &&
      !(quantities?.upcoming.remaining.knowledgeGraph > 0);
    return (
      showAlert && (
        <Alert severity="warning" key="scheduledUpdateAlert">
          You are <b>exceeding</b> the number of Knowledge Graphs included in your subscription&apos;s{' '}
          <b>scheduled update</b> ({formatQuantity(quantities.upcoming.knowledgeGraph, 'Knowledge Graph')}), which will
          take effect with the next billing period on <b>{formatDate(upcomingConfiguration.startDate, true)}</b>.
          <p>
            The subscription&apos;s scheduled update will be <b>removed</b>.
          </p>
        </Alert>
      )
    );
  }, [quantities, remainingQuantityIsSufficient, upcomingConfiguration]);
  // the alerts to pass down to the subcomponents
  const alerts = useMemo(
    () => <div className={styles.alerts}>{[alert].concat(scheduledUpdateAlert || [])}</div>,
    [alert, scheduledUpdateAlert]
  );

  const isExcelConnector = connector?.id === excelConnector.id;

  const inApplicationConnectorStep = useMemo(
    () => !isExcelConnector && connector?.type !== 'DATABASE_CONNECTOR',
    [connector]
  );

  const thirdStep = useMemo(() => {
    if (step < 2) return 'Connector';
    if (isExcelConnector) return 'Import Excel';
    if (inApplicationConnectorStep) return 'Application Connector';
    return 'Add Database Credentials';
  }, [connector, step, inApplicationConnectorStep]);

  const steps = useMemo(() => ['Choose Data Source', thirdStep, 'Select Data'], [thirdStep]);

  const showModal = useCallback(message => setModal({ open: true, message }), []);

  const selectConnector = useCallback(
    connector => {
      trackEvent('Data Source Selected', { connector: { id: connector.id } });
      setConnector(connector);
      setStep(step => step + 1);
    },
    [setConnector, setStep]
  );

  const fetchConnectors = useCallback(async () => {
    if (!connectors) {
      const result = await services.getConnectors();
      if (result.success) {
        const connectors = result.data.data.map(({ data }) => data);
        setConnectors([...connectors, excelConnector]);
      }
    }
  }, []);

  const getSubscription = async () => {
    // get all subscriptions and use the first one
    const response = await services.getSubscriptions();
    if (response.success) {
      const subscriptions = response.data;
      setSubscription(subscriptions.data[0]);
    }
  };

  // initially retrieves the subscription
  useEffect(() => {
    getSubscription();
  }, []);

  // When we create a new KG, immediately re-fetch the list of KGs and AFTER THAT
  // we set it as selected KG in the params and in State.
  useEffect(() => {
    const updateActiveKnowledgeGraph = async () => {
      const params = new URLSearchParams(location.search);
      const paramId = params.get('id');
      if (knowledgeGraph?.id && knowledgeGraph.id !== paramId && !fetchingKnowledgeGraphs) {
        setFetchingKnowledgeGraphs(true);
        await fetchKnowledgeGraphs();
        params.set('id', knowledgeGraph.id);
        history.replace({
          pathname: location.pathname,
          search: '?' + params.toString(),
          hash: location.hash
        });
        setFetchingKnowledgeGraphs(false);
      }
    };
    updateActiveKnowledgeGraph();
  }, [knowledgeGraph, location, history, fetchingKnowledgeGraphs, fetchKnowledgeGraphs]);

  useEffect(() => {
    fetchConnectors();
  }, [fetchConnectors]);

  return (
    <Content loading={!subscription}>
      <div className={styles.stepper}>
        <LabeledStepper step={step} steps={steps} />
      </div>
      {step === 1 && <ChooseDataSource selectConnector={selectConnector} connectors={connectors} alerts={alerts} />}
      {step === 2 && (
        <CreateKnowledgeGraph
          connectors={connectors}
          connector={connector}
          knowledgeGraph={knowledgeGraph}
          setKnowledgeGraph={setKnowledgeGraph}
          setStep={setStep}
          showModal={showModal}
          languages={languages}
          alerts={alerts}
          orgSettings={orgSettings}
          disableCreation={!remainingQuantityIsSufficient}
          nextButtonTooltip={nextButtonTooltip}
        />
      )}
      {step === 3 && (
        <ImportTables
          connector={connector}
          knowledgeGraph={knowledgeGraph}
          // going back from this step is not allowed for Excel connector as file import DB is already set up
          allowBack={!isExcelConnector}
          setStep={setStep}
          editorRef={editorRef}
        />
      )}
      <Dialog
        maxWidth="xs"
        fullWidth
        open={modal.open}
        onClose={() => setModal(prevModal => ({ ...prevModal, open: false }))}
      >
        <DialogTitle>Database Connection</DialogTitle>
        <DialogContent>{modal.message}</DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => setModal(prevModal => ({ ...prevModal, open: false }))}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {step > 1 &&
        !inApplicationConnectorStep && ( // We need to be able to redirect to Fivetran for application connectors
          <Beforeunload
            onBeforeunload={() =>
              'You have not finished creating your Knowledge Graph, are you sure you want to leave?'
            }
          />
        )}
    </Content>
  );
};

export default NewKnowledgeGraph;
